import React, { useCallback, useEffect, useRef } from 'react';
import { useAppStore } from '../../store/useAppStore';
import { FRAME_OFFSET, RULER_H } from './timelineConstants';

interface TimelineRulerProps {
  trackAreaW: number;
  pxPerFrame: number;
  scrollLeft: number;
  totalFrames: number;
  currentFrame: number;
  fps: number;
  inPoint: number | null;
  outPoint: number | null;
  isPlaying: boolean;
}

const TimelineRuler = React.memo(({
  trackAreaW, pxPerFrame, scrollLeft, totalFrames, currentFrame,
  fps, inPoint, outPoint, isPlaying,
}: TimelineRulerProps) => {
  const rulerRef          = useRef<HTMLCanvasElement>(null);
  const isDragging        = useRef(false);
  const rulerDragModeRef  = useRef<'scrub' | 'inPoint' | 'outPoint'>('scrub');
  const prevCanvasSizeRef = useRef({ w: 0, h: 0, dpr: 1 });
  const setCurrentFrame  = useAppStore((s) => s.setCurrentFrame);
  const setInPoint       = useAppStore((s) => s.setInPoint);
  const setOutPoint      = useAppStore((s) => s.setOutPoint);
  const setIsPlaying     = useAppStore((s) => s.setIsPlaying);

  // ── Canvas draw ───────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = rulerRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    const ps = prevCanvasSizeRef.current;
    if (w !== ps.w || h !== ps.h || dpr !== ps.dpr) {
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
      ctx.scale(dpr, dpr);
      prevCanvasSizeRef.current = { w, h, dpr };
    }

    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, w, h);

    const candidates = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 1800, 3600];
    const majorStep  = candidates.find((c) => c * pxPerFrame >= 80) ?? candidates[candidates.length - 1];
    const minorStep  = majorStep >= 10 ? majorStep / 5 : 1;
    const firstFrame = Math.floor(scrollLeft / pxPerFrame);
    const lastFrame  = Math.ceil((scrollLeft + w) / pxPerFrame);

    ctx.font         = '10px monospace';
    ctx.textBaseline = 'bottom';

    for (let f = Math.floor(firstFrame / minorStep) * minorStep; f <= Math.min(lastFrame, totalFrames); f += minorStep) {
      const x       = f * pxPerFrame - scrollLeft;
      const isMajor = f % majorStep === 0;
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(x, h - (isMajor ? 10 : 5));
      ctx.strokeStyle = isMajor ? '#555' : '#333';
      ctx.lineWidth   = 1;
      ctx.stroke();
      if (isMajor) {
        const sec   = f / fps;
        const label = sec % 1 === 0 ? `${sec}s` : `f${f + FRAME_OFFSET}`;
        ctx.fillStyle = '#777';
        ctx.fillText(label, x + 2, h - 10);
      }
    }

    // ── In/out range shading + bracket markers ────────────────────────────────
    if (inPoint !== null || outPoint !== null) {
      const x0 = (inPoint ?? 0) * pxPerFrame - scrollLeft;
      const x1 = ((outPoint ?? totalFrames - 1) + 1) * pxPerFrame - scrollLeft;
      if (x1 > x0) {
        ctx.fillStyle = 'rgba(255,255,255,0.05)';
        ctx.fillRect(Math.max(0, x0), 0, Math.min(w, x1) - Math.max(0, x0), h);
      }
    }
    if (inPoint !== null) {
      const ix = inPoint * pxPerFrame - scrollLeft;
      ctx.fillStyle = '#22c55e';
      ctx.fillRect(ix, 0, 2, h);
      ctx.fillRect(ix, 0, 9, 2);
    }
    if (outPoint !== null) {
      const ox = outPoint * pxPerFrame - scrollLeft;
      ctx.fillStyle = '#f87171';
      ctx.fillRect(ox - 1, 0, 2, h);
      ctx.fillRect(ox - 8, 0, 9, 2);
    }

    // Playhead on ruler
    const hx = Math.round(currentFrame) * pxPerFrame - scrollLeft;
    ctx.fillStyle = '#f5a623';
    ctx.beginPath();
    ctx.moveTo(hx - 5, 0);
    ctx.lineTo(hx + 5, 0);
    ctx.lineTo(hx, 8);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(hx, 0);
    ctx.lineTo(hx, h);
    ctx.strokeStyle = '#f5a623';
    ctx.lineWidth   = 2;
    ctx.stroke();
  }, [trackAreaW, pxPerFrame, fps, totalFrames, currentFrame, scrollLeft, inPoint, outPoint]);

  // ── Pointer handlers ──────────────────────────────────────────────────────
  const frameFromRuler = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = rulerRef.current;
      if (!canvas) return 0;
      const x = e.clientX - canvas.getBoundingClientRect().left + scrollLeft;
      return Math.max(0, Math.min(totalFrames - 1, Math.round(x / pxPerFrame)));
    },
    [scrollLeft, pxPerFrame, totalFrames],
  );

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    isDragging.current = true;
    (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
    if (isPlaying) setIsPlaying(false);
    const canvas = rulerRef.current;
    const mouseX = e.clientX - (canvas?.getBoundingClientRect().left ?? 0) + scrollLeft;
    if (inPoint !== null && Math.abs(mouseX - inPoint * pxPerFrame) < 10) {
      rulerDragModeRef.current = 'inPoint';
    } else if (outPoint !== null && Math.abs(mouseX - outPoint * pxPerFrame) < 10) {
      rulerDragModeRef.current = 'outPoint';
    } else {
      rulerDragModeRef.current = 'scrub';
      setCurrentFrame(frameFromRuler(e));
    }
  }, [frameFromRuler, inPoint, outPoint, isPlaying, pxPerFrame, scrollLeft, setCurrentFrame, setIsPlaying]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDragging.current) return;
    const frame = frameFromRuler(e);
    if (rulerDragModeRef.current === 'inPoint') {
      setInPoint(Math.min(frame, outPoint !== null ? outPoint - 1 : totalFrames - 1));
    } else if (rulerDragModeRef.current === 'outPoint') {
      setOutPoint(Math.max(frame, inPoint !== null ? inPoint + 1 : 0));
    } else {
      setCurrentFrame(frame);
    }
  }, [frameFromRuler, inPoint, outPoint, setCurrentFrame, setInPoint, setOutPoint, totalFrames]);

  const onPointerUp = useCallback(() => {
    isDragging.current = false;
    rulerDragModeRef.current = 'scrub';
  }, []);

  return (
    <canvas
      ref={rulerRef}
      style={{ display: 'block', height: RULER_H, width: '100%', flexShrink: 0, cursor: 'col-resize', borderBottom: '1px solid #2a2a40' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
    />
  );
});

export default TimelineRuler;
